import React from "react";
import {
  AbsoluteFill,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { useCopy } from "../copy";
import { LOOK, TYPE } from "../look";
import { StoreBadge } from "../ui/bits";
import { Words } from "../ui/Words";
import { Sfx } from "../../b2b/ui/Sfx";

/**
 * End card — the landing hero promise, verbatim ("Gagne de l'argent en
 * recommandant ce que tu aimes."), the three objections answered in one
 * line, and the stores. Quiet hold at the end so a paused last frame
 * reads as a finished CTA.
 */
export const Cta: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const copy = useCopy();

  const logo = spring({ frame, fps, config: { damping: 11, stiffness: 200, mass: 0.7 } });
  const sub = spring({ frame: frame - 26, fps, config: { damping: 18, stiffness: 180 } });
  const badges = spring({ frame: frame - 32, fps, config: { damping: 14, stiffness: 200, mass: 0.7 } });

  // A single sheen sweep across the badges once they have settled.
  const sheen = interpolate(frame, [52, 72], [-30, 130], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        background: `radial-gradient(120% 90% at 50% 0%, ${LOOK.blue} 55%, ${LOOK.blueDeep} 100%)`,
        alignItems: "center",
        justifyContent: "center",
        gap: 0,
      }}
    >
      <div style={{ scale: logo, marginBottom: 48 }}>
        {/* The shipped SVG is brand blue; flip it to white on blue. */}
        <Img
          src={staticFile("logo-frak.svg")}
          style={{ width: 250, height: (250 * 28) / 73, filter: "brightness(0) invert(1)" }}
        />
      </div>

      <Words text={copy.cta.line1} start={6} fontSize={TYPE.hero - 16} color={LOOK.white} />
      <Words
        text={copy.cta.line2}
        start={12}
        stagger={2}
        fontSize={TYPE.support + 10}
        color={LOOK.white}
        style={{ marginTop: 14, maxWidth: 880 }}
      />

      <div
        style={{
          marginTop: 36,
          fontFamily: LOOK.body,
          fontWeight: LOOK.medium,
          fontSize: 36,
          color: LOOK.sky,
          opacity: sub,
          translate: `0 ${(1 - sub) * 20}px`,
        }}
      >
        {copy.cta.sub}
      </div>

      <div
        style={{
          marginTop: 60,
          display: "flex",
          gap: 24,
          position: "relative",
          overflow: "hidden",
          padding: 4,
          borderRadius: 26,
          opacity: badges,
          translate: `0 ${(1 - badges) * 60}px`,
        }}
      >
        <StoreBadge store="apple" small={copy.cta.appStoreSmall} name={copy.cta.appStore} />
        <StoreBadge store="google" small={copy.cta.playSmall} name={copy.cta.play} />
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${sheen}%`,
            width: "18%",
            background: "linear-gradient(100deg, rgba(255,255,255,0), rgba(255,255,255,0.35), rgba(255,255,255,0))",
            pointerEvents: "none",
          }}
        />
      </div>

      <Sfx sound="pop" at={34} volume={0.7} />
      <Sfx sound="sparkle" at={60} volume={0.7} />
    </AbsoluteFill>
  );
};
